import type {
  CaptionLayout,
  OverlayClip,
  SceneDocument,
  Timeline,
  WordOverride,
} from "../scene/schema";
import { layoutClips, overlayClips, timelineTimeAt } from "./timeline";

/**
 * Automatic captions (docs/05-video-und-untertitel.md, "Warum Quellzeit so
 * wichtig ist"): transcripts belong to a media file and are stored in source
 * time. The caption track is derived from the main track on every render, so
 * cut-out words disappear and moved clips take their captions with them.
 */

/** A word of a transcript in source time (ms). */
export interface TranscriptWord {
  id: string;
  text: string;
  start: number;
  end: number;
  /** 0..1 */
  confidence?: number;
}

/** Transcript words per asset id (missing = not transcribed yet). */
export type TranscriptMap = Readonly<Record<string, readonly TranscriptWord[] | undefined>>;

/** A word placed on the timeline, including hidden ones (transcript editor). */
export interface TimelineWord {
  /** `assetId:wordId`, the key of corrections in `timeline.captions.overrides`. */
  key: string;
  assetId: string;
  wordId: string;
  clipId: string;
  /** Text after corrections. */
  text: string;
  /** Text as recognised. */
  original: string;
  start: number;
  end: number;
  sourceStart: number;
  sourceEnd: number;
  hidden: boolean;
  breakBefore: boolean;
  confidence?: number;
  /** Emphasised keyword (set only when true). */
  emphasis?: true;
}

/** A word as shown in the captions. */
export interface CaptionWord {
  key: string;
  text: string;
  start: number;
  end: number;
  breakBefore: boolean;
  /** Emphasised keyword (set only when true). */
  emphasis?: true;
}

export interface CaptionPage {
  index: number;
  start: number;
  end: number;
  lines: CaptionWord[][];
}

export function wordKey(assetId: string, wordId: string): string {
  return `${assetId}:${wordId}`;
}

/**
 * Speaker clips on the tracks above: videos that are not shown in a device
 * (the speaker's circle or half of a split screen next to a phone). They
 * give the captions when the main track has no spoken words; screen
 * recordings in devices never do.
 */
function speakerOverlays(timeline: Pick<Timeline, "overlays"> | undefined): OverlayClip[] {
  return overlayClips(timeline)
    .filter((clip) => clip.kind === "video" && !clip.device && !clip.muted && clip.volume > 0)
    .sort((a, b) => a.start - b.start || a.track - b.track);
}

/** Videos whose transcripts the captions can use (main track, then speaker clips above). */
export function captionSourceAssetIds(
  timeline: Pick<Timeline, "clips" | "overlays"> | undefined,
): string[] {
  return [
    ...new Set([
      ...(timeline?.clips ?? []).filter((clip) => clip.kind === "video").map((c) => c.assetId),
      ...speakerOverlays(timeline).map((clip) => clip.assetId),
    ]),
  ];
}

/**
 * All transcript words of the main track in timeline time, in playback
 * order – or, when nobody speaks there, those of the speaker clips above
 * (`speakerOverlays`).
 */
export function mapTranscriptWords(doc: SceneDocument, transcripts: TranscriptMap): TimelineWord[] {
  const overrides = doc.timeline?.captions.overrides ?? {};
  const out: TimelineWord[] = [];
  const push = (
    clip: { id: string; assetId: string; sourceIn: number; sourceOut: number },
    word: TranscriptWord,
    at: (source: number) => number,
  ) => {
    const key = wordKey(clip.assetId, word.id);
    const override = overrides[key];
    const text = override?.text ?? word.text;
    out.push({
      key,
      assetId: clip.assetId,
      wordId: word.id,
      clipId: clip.id,
      text,
      original: word.text,
      start: at(Math.max(word.start, clip.sourceIn)),
      end: at(Math.min(word.end, clip.sourceOut)),
      sourceStart: word.start,
      sourceEnd: word.end,
      hidden: Boolean(override?.hidden) || text.trim() === "",
      breakBefore: Boolean(override?.breakBefore),
      ...(word.confidence !== undefined ? { confidence: word.confidence } : {}),
      ...(override?.emphasis ? { emphasis: true as const } : {}),
    });
  };
  const inClip = (
    word: { start: number; end: number },
    clip: { sourceIn: number; sourceOut: number },
  ) => {
    const middle = (word.start + word.end) / 2;
    return middle >= clip.sourceIn && middle < clip.sourceOut;
  };
  for (const entry of layoutClips(doc.timeline)) {
    const { clip } = entry;
    if (clip.kind !== "video" || clip.muted || clip.volume === 0) continue;
    for (const word of transcripts[clip.assetId] ?? []) {
      if (inClip(word, clip)) push(clip, word, (source) => timelineTimeAt(entry, source));
    }
  }
  if (out.length > 0) return out;
  for (const clip of speakerOverlays(doc.timeline)) {
    for (const word of transcripts[clip.assetId] ?? []) {
      if (!inClip(word, clip)) continue;
      push(clip, word, (source) => clip.start + (source - clip.sourceIn) / clip.speed);
    }
  }
  return out.sort((a, b) => a.start - b.start);
}

/**
 * Visible caption words. A correction with several words ("Reels" → "Instagram
 * Reels") splits the time by character length.
 */
export function captionWords(doc: SceneDocument, transcripts: TranscriptMap): CaptionWord[] {
  const out: CaptionWord[] = [];
  for (const word of mapTranscriptWords(doc, transcripts)) {
    if (word.hidden) continue;
    const parts = word.text.trim().split(/\s+/).filter(Boolean);
    if (parts.length <= 1) {
      out.push({
        key: word.key,
        text: word.text.trim(),
        start: word.start,
        end: word.end,
        breakBefore: word.breakBefore,
        ...(word.emphasis ? { emphasis: true as const } : {}),
      });
      continue;
    }
    const total = parts.reduce((sum, part) => sum + part.length, 0);
    let cursor = word.start;
    parts.forEach((part, index) => {
      const length = ((word.end - word.start) * part.length) / total;
      out.push({
        key: `${word.key}:${index}`,
        text: part,
        start: cursor,
        end: cursor + length,
        breakBefore: index === 0 && word.breakBefore,
        ...(word.emphasis ? { emphasis: true as const } : {}),
      });
      cursor += length;
    });
  }
  return out;
}

const SENTENCE_END = /[.!?…]["»“”')\]]*$/;
const CLAUSE_END = /[,;:–—]$/;

/** Hold time after the last word of a caption. */
const HOLD_MS = 300;
/** Pauses shorter than this keep the caption on screen until the next one. */
const BRIDGE_MS = 600;

/** Groups words into captions (what is visible at the same time) and lines. */
export function paginateCaptions(
  words: readonly CaptionWord[],
  layout: CaptionLayout,
): CaptionPage[] {
  const pages: CaptionWord[][][] = [];
  let lines: CaptionWord[][] = [];
  let line: CaptionWord[] = [];
  let lineChars = 0;
  const flushLine = () => {
    if (line.length > 0) lines.push(line);
    line = [];
    lineChars = 0;
  };
  const flushPage = () => {
    flushLine();
    if (lines.length > 0) pages.push(lines);
    lines = [];
  };
  let previous: CaptionWord | undefined;
  for (const word of words) {
    const length = word.text.length;
    if (previous) {
      const pause = word.start - previous.end;
      if (word.breakBefore || pause >= layout.pauseMs || SENTENCE_END.test(previous.text)) {
        flushPage();
      } else {
        const fitsLine =
          line.length < layout.maxWordsPerLine && lineChars + 1 + length <= layout.maxCharsPerLine;
        const clauseBreak = CLAUSE_END.test(previous.text) && line.length >= 2;
        if (!fitsLine || clauseBreak) {
          if (lines.length + 1 < layout.maxLines) flushLine();
          else flushPage();
        }
      }
    }
    line.push(word);
    lineChars += (line.length > 1 ? 1 : 0) + length;
    previous = word;
  }
  flushPage();
  balanceOrphans(pages, layout);

  const out: CaptionPage[] = [];
  for (const [index, pageLines] of pages.entries()) {
    const first = pageLines[0]![0]!;
    const lastLine = pageLines[pageLines.length - 1]!;
    const last = lastLine[lastLine.length - 1]!;
    out.push({ index, start: first.start, end: last.end, lines: pageLines });
  }
  for (const [index, page] of out.entries()) {
    const next = out[index + 1];
    const lastWordEnd = page.end;
    const wanted = Math.max(lastWordEnd + HOLD_MS, page.start + layout.minPageMs);
    if (next && next.start - lastWordEnd < BRIDGE_MS) page.end = next.start;
    else page.end = next ? Math.min(wanted, next.start) : wanted;
  }
  return out;
}

/** Moves a word from a full caption to a following caption that would hold a single word. */
function balanceOrphans(pages: CaptionWord[][][], layout: CaptionLayout) {
  for (let index = 1; index < pages.length; index++) {
    const page = pages[index]!;
    const words = page.flat();
    if (words.length !== 1 || words[0]!.breakBefore) continue;
    const previousPage = pages[index - 1]!;
    const previousWords = previousPage.flat();
    const lastWord = previousWords[previousWords.length - 1]!;
    if (previousWords.length < 3 || SENTENCE_END.test(lastWord.text)) continue;
    if (words[0]!.start - lastWord.end >= layout.pauseMs) continue;
    const lastLine = previousPage[previousPage.length - 1]!;
    const moved = lastLine.pop()!;
    if (lastLine.length === 0) previousPage.pop();
    page[0]!.unshift(moved);
  }
}

/** The caption visible at `t` (binary search). */
export function captionPageAt(pages: readonly CaptionPage[], t: number): CaptionPage | null {
  let low = 0;
  let high = pages.length - 1;
  while (low <= high) {
    const mid = (low + high) >> 1;
    const page = pages[mid]!;
    if (t < page.start) high = mid - 1;
    else if (t >= page.end) low = mid + 1;
    else return page;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Caption list of the editor: one row per caption, edited as plain text
// ---------------------------------------------------------------------------

/** A caption in the editor's list, with the hidden words around it. */
export interface CaptionRow {
  /** Key of the first word (stays the same while the text is corrected). */
  key: string;
  /** The caption as shown; null when every word of the row is hidden. */
  page: CaptionPage | null;
  /** All words of the row in playback order, hidden ones included. */
  words: TimelineWord[];
  start: number;
  end: number;
}

/**
 * Groups the transcript words by the caption they appear in. Hidden words
 * join the caption closest in time, so they can be shown and restored there.
 */
export function captionRows(
  words: readonly TimelineWord[],
  pages: readonly CaptionPage[],
): CaptionRow[] {
  const pageOf = new Map<string, CaptionPage>();
  for (const page of pages) {
    for (const word of page.lines.flat()) pageOf.set(word.key, page);
  }
  const rows: CaptionRow[] = pages.map((page) => ({
    key: "",
    page,
    words: [],
    start: page.start,
    end: page.end,
  }));
  const rowOf = new Map(pages.map((page, index) => [page, rows[index]!]));
  let previous: { row: CaptionRow; end: number } | null = null;
  let pending: TimelineWord[] = [];
  const place = (next: { row: CaptionRow; start: number } | null) => {
    for (const word of pending) {
      const toPrevious = previous && (!next || word.start - previous.end < next.start - word.end);
      const target = toPrevious ? previous!.row : next?.row;
      target?.words.push(word);
    }
    pending = [];
  };
  for (const word of words) {
    const page = word.hidden ? undefined : (pageOf.get(word.key) ?? pageOf.get(`${word.key}:0`));
    const row = page ? rowOf.get(page) : undefined;
    if (!row) {
      pending.push(word);
      continue;
    }
    place({ row, start: word.start });
    row.words.push(word);
    previous = { row, end: word.end };
  }
  if (previous) place(null);
  // Nothing visible at all: the hidden words form a row of their own.
  if (pending.length > 0) {
    rows.push({
      key: "",
      page: null,
      words: pending,
      start: pending[0]!.start,
      end: pending[pending.length - 1]!.end,
    });
  }
  const out = rows.filter((row) => row.words.length > 0);
  for (const row of out) row.key = row.words[0]!.key;
  return out;
}

/** Text of the visible words of a row, as the list shows it for editing. */
export function captionRowText(words: readonly TimelineWord[]): string {
  return words
    .filter((word) => !word.hidden)
    .map((word) => word.text.trim())
    .filter(Boolean)
    .join(" ");
}

export interface CaptionTextEdit {
  /** Corrections to apply per word key (`undefined` removes a field). */
  patches: Record<string, Pick<WordOverride, "text" | "hidden">>;
  /** For every token of the new text: index of the visible word it belongs to. */
  owners: number[];
}

/**
 * Turns a corrected caption text back into word corrections. The words keep
 * their timing: unchanged tokens stay with their word, replaced ones take the
 * place of the old ones in order, added ones join the word before them, and a
 * word without any text left is hidden.
 */
export function editCaptionText(words: readonly TimelineWord[], text: string): CaptionTextEdit {
  const visible = words.filter((word) => !word.hidden);
  const before: { text: string; owner: number }[] = [];
  visible.forEach((word, owner) => {
    for (const token of word.text.trim().split(/\s+/).filter(Boolean)) {
      before.push({ text: token, owner });
    }
  });
  const after = text.trim().split(/\s+/).filter(Boolean);
  const owners = alignTokens(
    before.map((token) => token.text),
    after,
  ).map((index) => (index === null ? null : before[index]!.owner));
  // Tokens added at the very start belong to the first word.
  let fallback = before[0]?.owner ?? 0;
  const resolved: number[] = [];
  owners.forEach((owner, index) => {
    if (owner !== null) fallback = owner;
    resolved[index] = owner ?? fallback;
  });
  const tokens = visible.map((): string[] => []);
  for (const [index, owner] of resolved.entries()) tokens[owner]?.push(after[index]!);
  const patches: CaptionTextEdit["patches"] = {};
  visible.forEach((word, index) => {
    const next = tokens[index]!.join(" ");
    if (next === word.text.trim()) return;
    patches[word.key] =
      next === ""
        ? { text: undefined, hidden: true }
        : { text: next === word.original ? undefined : next, hidden: undefined };
  });
  return { patches, owners: resolved };
}

/** Character ranges of the whitespace-separated tokens of a text. */
function tokenSpans(text: string): { start: number; end: number }[] {
  return [...text.matchAll(/\S+/g)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
  }));
}

/**
 * "Neuer Untertitel ab hier": the visible word that starts a new caption when
 * the text is split at `offset` (the word at or after it), or null at the start.
 */
export function captionSplitWord(
  words: readonly TimelineWord[],
  text: string,
  offset: number,
): TimelineWord | null {
  const visible = words.filter((word) => !word.hidden);
  const { owners } = editCaptionText(words, text);
  const spans = tokenSpans(text);
  let token = spans.findIndex((span) => span.end > offset);
  if (token < 0) return null;
  // A word corrected into several can only start a caption with its first part.
  while (token > 0 && owners[token] === owners[token - 1]) token++;
  const owner = owners[token];
  if (owner === undefined || owner === 0) return null;
  return visible[owner] ?? null;
}

/** Visible words touched by the selection [start, end) of the (edited) caption text. */
export function captionWordsInSelection(
  words: readonly TimelineWord[],
  text: string,
  start: number,
  end: number,
): TimelineWord[] {
  const visible = words.filter((word) => !word.hidden);
  const { owners } = editCaptionText(words, text);
  const touched = new Set<number>();
  tokenSpans(text).forEach((span, index) => {
    if (span.end > start && span.start < end && owners[index] !== undefined) {
      touched.add(owners[index]);
    }
  });
  return visible.filter((_, index) => touched.has(index));
}

/** A token without case and surrounding punctuation ("Zuerst," and "zuerst" are the same word). */
function comparable(token: string): string {
  return token.toLocaleLowerCase("de-DE").replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "") || token;
}

/**
 * For every token of `after`: the index of the token of `before` it replaces
 * or keeps, or null for an added token (then it joins the token before it).
 * The same words, ignoring case and punctuation, are matched by their longest
 * common subsequence, so a fixed capital letter stays with its word.
 */
function alignTokens(beforeTokens: readonly string[], afterTokens: readonly string[]) {
  const before = beforeTokens.map(comparable);
  const after = afterTokens.map(comparable);
  const n = before.length;
  const m = after.length;
  const lcs = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) {
      lcs[i]![j] =
        before[i] === after[j]
          ? lcs[i + 1]![j + 1]! + 1
          : Math.max(lcs[i + 1]![j]!, lcs[i]![j + 1]!);
    }
  }
  const out: (number | null)[] = new Array(m).fill(null);
  // Changed stretches between unchanged tokens: pair them up in order.
  const pair = (fromI: number, toI: number, fromJ: number, toJ: number) => {
    for (let j = fromJ; j < toJ; j++) {
      const i = fromI + (j - fromJ);
      out[j] = i < toI ? i : toI > fromI ? toI - 1 : null;
    }
  };
  let i = 0;
  let j = 0;
  let gapI = 0;
  let gapJ = 0;
  while (i < n && j < m) {
    if (before[i] === after[j]) {
      pair(gapI, i, gapJ, j);
      out[j] = i;
      i++;
      j++;
      gapI = i;
      gapJ = j;
    } else if (lcs[i + 1]![j]! >= lcs[i]![j + 1]!) i++;
    else j++;
  }
  pair(gapI, n, gapJ, m);
  return out;
}

export function captionText(page: CaptionPage, uppercase = false): string {
  const text = page.lines.map((line) => line.map((word) => word.text).join(" ")).join("\n");
  return uppercase ? text.toLocaleUpperCase("de-DE") : text;
}

function timestamp(ms: number, separator: "," | "."): string {
  const clamped = Math.max(0, Math.round(ms));
  const hours = Math.floor(clamped / 3_600_000);
  const minutes = Math.floor((clamped % 3_600_000) / 60_000);
  const seconds = Math.floor((clamped % 60_000) / 1000);
  const millis = clamped % 1000;
  const pad = (value: number, length = 2) => String(value).padStart(length, "0");
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}${separator}${pad(millis, 3)}`;
}

/** SubRip subtitles (YouTube, LinkedIn, Facebook). */
export function captionsToSrt(pages: readonly CaptionPage[], uppercase = false): string {
  return pages
    .map(
      (page, index) =>
        `${index + 1}\n${timestamp(page.start, ",")} --> ${timestamp(page.end, ",")}\n${captionText(page, uppercase)}\n`,
    )
    .join("\n");
}

/** WebVTT subtitles (web players). */
export function captionsToVtt(pages: readonly CaptionPage[], uppercase = false): string {
  const cues = pages
    .map(
      (page) =>
        `${timestamp(page.start, ".")} --> ${timestamp(page.end, ".")}\n${captionText(page, uppercase)}\n`,
    )
    .join("\n");
  return `WEBVTT\n\n${cues}`;
}

/**
 * Timeline ranges between words that are longer than `minPauseMs` within
 * `[from, to)` ("Stille entfernen"). `paddingMs` stays around the speech.
 */
export function silenceRanges(
  words: readonly { start: number; end: number }[],
  to: number,
  minPauseMs: number,
  paddingMs: number,
  from = 0,
): { start: number; end: number }[] {
  const ranges: { start: number; end: number }[] = [];
  const sorted = [...words].sort((a, b) => a.start - b.start);
  let cursor = from;
  for (const word of sorted) {
    const gapStart = cursor === from ? from : cursor + paddingMs;
    const gapEnd = word.start - paddingMs;
    const pause = word.start - cursor;
    if (pause >= minPauseMs && gapEnd - gapStart >= 100)
      ranges.push({ start: gapStart, end: gapEnd });
    cursor = Math.max(cursor, word.end);
  }
  if (sorted.length > 0 && to - cursor >= minPauseMs) {
    ranges.push({ start: cursor + paddingMs, end: to });
  }
  return ranges.filter((range) => range.end - range.start >= 100);
}

/**
 * Pauses inside the clips that have spoken words. Stills, muted clips and
 * videos without a transcript are never cut: silence there is intended
 * (title images, B-roll) or simply not known yet.
 */
export function silenceRangesInClips(
  doc: SceneDocument,
  transcripts: TranscriptMap,
  minPauseMs: number,
  paddingMs: number,
): { start: number; end: number }[] {
  const byClip = new Map<string, TimelineWord[]>();
  for (const word of mapTranscriptWords(doc, transcripts)) {
    const list = byClip.get(word.clipId) ?? [];
    list.push(word);
    byClip.set(word.clipId, list);
  }
  return layoutClips(doc.timeline).flatMap((entry) => {
    const words = byClip.get(entry.clip.id);
    return words ? silenceRanges(words, entry.end, minPauseMs, paddingMs, entry.start) : [];
  });
}
